// Briefing workspace — adapted from
// inku/frontend/web/src/components/briefing/BriefingLayout.tsx.
//
// Split layout: chat panel on the left (our IntakePane), main content on
// the right (Moodboard or Canvases view). Top ProjectBar has a view
// switcher + Brief button. Brief content opens in a slide-out sheet.
//
// Both "Briefing" and "Briefing V2" land here so the visual experience
// is unified — Briefing V2 just defaults to having the agent intake
// conversation seeded in the chat panel.

import {
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  Layers,
  Loader2,
  Palette,
  Plus,
  Sparkles,
  Star,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { IntakePane } from "@/components/IntakePane";
import { Lightbox } from "@/components/Lightbox";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useStore } from "@/lib/store";
import type { Briefing, SavedProductCatalog, StreamEvent } from "@/lib/types";
import { cn } from "@/lib/utils";

type View = "moodboard" | "canvases";
type AssetFilter = "all" | "World" | "Character" | "Product" | "Lifestyle";
type MoodboardTab =
  | AssetFilter
  | "creative_direction"
  | "briefing"
  | "shop_the_looks";

interface MoodboardAsset {
  id: string;
  type: "World" | "Character" | "Product" | "Lifestyle";
  label: string;
  thumb: string;
  status: "approved" | "pending";
}

// Moodboard assets and palettes are populated by the agent (image_director
// subagent + future palette extraction). Until those wires are in place,
// the panels render an empty state instead of fake gradient cards — those
// were demo-time placeholders that confused real users into thinking the
// agent had produced output it hadn't.
const MOOD_ASSETS: MoodboardAsset[] = [];

const PALETTES: { id: string; name: string; colors: string[] }[] = [];

// Per-variant generation card. Lives only in component state (not
// localStorage) — refreshing the page resets in-flight cards. Completed
// generations remain visible because they're also persisted as `images`
// artifacts on the assistant message; we'll backfill from there in v2.
interface GenerationSlot {
  slot_id: string;
  status: "generating" | "ready" | "failed";
  startedAt: number;
  prompt?: string;
  aspect_ratio?: string;
  url?: string;
  generation_id?: string;
  error?: string;
  /** Latest backend phase verbatim ("submitting" / "submitted" / "running"
   * / Luma's poll states) — used as a tooltip on the loading card so the
   * user can tell "queued" from "uploading" without blocking on console. */
  phase?: string;
}

interface Props {
  briefing: Briefing;
  onRefetch: () => void;
}

export function BriefingWorkspaceLayout({ briefing, onRefetch }: Props) {
  const { navigate } = useStore();
  const [view, setView] = useState<View>("moodboard");
  const [briefOpen, setBriefOpen] = useState(false);
  const [tab, setTab] = useState<MoodboardTab>("all");
  const [preview, setPreview] = useState<MoodboardAsset | null>(null);

  // Live artifacts streamed up from the IntakePane. The persisted record
  // (briefing.cd_brief / briefing.brief) is canonical, but it lags the
  // SSE stream by a refetch. We surface live data immediately so the
  // Creative Direction / Briefing tabs update as the agent emits.
  const [liveCdBrief, setLiveCdBrief] = useState<unknown>(null);
  const [liveBrief, setLiveBrief] = useState<unknown>(null);

  // Per-variant generation cards shown in the moodboard. Keyed by the
  // backend's slot_id so phases (submitting → submitted → ready/failed)
  // mutate the same card across the lifetime of one generation.
  const [generations, setGenerations] = useState<
    Record<string, GenerationSlot>
  >({});

  // Auto-switch tracking. First time the agent emits a CD brief or a
  // marketing brief in this session, jump the active tab to that panel
  // so the user sees the deliverable land. Once flipped per type we
  // never auto-switch again — subsequent refinements respect whatever
  // tab the user is on.
  const seenCdBrief = useRef(false);
  const seenBrief = useRef(false);
  const seenFirstGeneration = useRef(false);
  const seenFirstCatalog = useRef(false);

  // Set of catalog ids that are already persisted on the briefing. Lets
  // ProductCatalogCard render "Saved" state instead of an active save
  // button when the catalog is already in the moodboard tab.
  const savedCatalogIds = useMemo(
    () =>
      new Set(
        (briefing.catalogs ?? [])
          .map((c) => c.id)
          .filter((id): id is string => typeof id === "string")
      ),
    [briefing.catalogs]
  );

  // First time a catalog actually lands on briefing.catalogs (post-save),
  // flip to the Shop the looks tab so the user sees the persistence
  // result without having to click around for it.
  useEffect(() => {
    if (!seenFirstCatalog.current && (briefing.catalogs?.length ?? 0) > 0) {
      seenFirstCatalog.current = true;
      setTab("shop_the_looks");
    }
  }, [briefing.catalogs]);

  const handleArtifact = (kind: string, data: unknown) => {
    if (kind === "cd_brief") {
      setLiveCdBrief(data);
      if (!seenCdBrief.current) {
        seenCdBrief.current = true;
        setTab("creative_direction");
      }
    } else if (kind === "brief") {
      setLiveBrief(data);
      if (!seenBrief.current) {
        seenBrief.current = true;
        setTab("briefing");
      }
    }
  };

  const handleGenerationProgress = (
    ev: Extract<StreamEvent, { type: "image_gen_progress" }>
  ) => {
    setGenerations((current) => {
      const prev = current[ev.slot_id];
      const next: GenerationSlot = {
        slot_id: ev.slot_id,
        status:
          ev.phase === "ready"
            ? "ready"
            : ev.phase === "failed"
              ? "failed"
              : "generating",
        startedAt: prev?.startedAt ?? Date.now(),
        prompt: ev.prompt ?? prev?.prompt,
        aspect_ratio: ev.aspect_ratio ?? prev?.aspect_ratio ?? "1:1",
        url: ev.url ?? prev?.url,
        generation_id: ev.generation_id ?? prev?.generation_id,
        error: ev.error ?? prev?.error,
        // The latest phase string is useful for tooltips ("queued",
        // "processing", "uploading"). Keep the verbatim value.
        phase: ev.phase,
      };
      return { ...current, [ev.slot_id]: next };
    });
    // First image of the session jumps the moodboard to "All" so the
    // user actually sees the cards. Subsequent calls respect whatever
    // tab they're on.
    if (!seenFirstGeneration.current) {
      seenFirstGeneration.current = true;
      setTab("all");
    }
  };

  // Intake mode = no marketing brief yet AND status is draft.
  const intakeMode = !briefing.brief && briefing.status === "draft";

  // Asset-filter tabs render the moodboard; the two extra tabs render
  // entirely different content (cd brief / marketing brief).
  const isAssetTab =
    tab !== "creative_direction" &&
    tab !== "briefing" &&
    tab !== "shop_the_looks";
  const filteredAssets =
    tab === "all" || !isAssetTab
      ? MOOD_ASSETS
      : MOOD_ASSETS.filter((a) => a.type === tab);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* ProjectBar */}
      <ProjectBar
        briefing={briefing}
        view={view}
        onViewChange={setView}
        onOpenBrief={() => setBriefOpen(true)}
        onBack={() => navigate("/briefings")}
      />

      {/* Split: chat left, main right */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
        {/* Chat panel (left) */}
        <div className="flex min-h-0 min-w-0 flex-col border-b border-border lg:w-[420px] lg:shrink-0 lg:border-b-0 lg:border-r">
          <IntakePane
            threadId={briefing.thread_id}
            briefingId={briefing.id}
            savedCatalogIds={savedCatalogIds}
            onBriefingUpdated={onRefetch}
            onArtifact={handleArtifact}
            onGenerationProgress={handleGenerationProgress}
          />
        </div>

        {/* Main view (right) */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-surface-subtle">
          {view === "moodboard" ? (
            <MoodboardView
              tab={tab}
              onTabChange={setTab}
              assets={filteredAssets}
              generations={generations}
              briefing={briefing}
              liveCdBrief={liveCdBrief}
              liveBrief={liveBrief}
              onPreview={(a) => setPreview(a)}
              onRefetch={onRefetch}
            />
          ) : (
            <CanvasesView />
          )}
        </main>
      </div>

      {/* Brief slide-out */}
      <BriefSheet
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        briefing={briefing}
        intakeMode={intakeMode}
      />

      {/* Asset preview */}
      <Lightbox
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.label ?? ""}
        subtitle={preview?.type}
        thumb={preview?.thumb ?? ""}
        meta={
          preview
            ? [
                { label: "Type", value: preview.type },
                { label: "Status", value: preview.status === "approved" ? "Approved" : "Pending" },
                { label: "Used in", value: "2 canvases" },
                { label: "Variants", value: "8" },
              ]
            : []
        }
      />
    </div>
  );
}

// ─── ProjectBar ──────────────────────────────────────────────────────
function ProjectBar({
  briefing,
  view,
  onViewChange,
  onOpenBrief,
  onBack,
}: {
  briefing: Briefing;
  view: View;
  onViewChange: (v: View) => void;
  onOpenBrief: () => void;
  onBack: () => void;
}) {
  return (
    <div className="border-b border-border bg-background">
      <div className="px-4 md:px-6 h-14 flex items-center gap-4">
        {/* Left: back + title */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm font-medium truncate">
            {briefing.title || "Untitled briefing"}
          </p>
          <StatusPill status={briefing.status} size="sm" />
        </div>

        {/* Center: view switcher */}
        <div className="flex-1 flex justify-center">
          <div className="inline-flex items-center rounded-full bg-muted/50 p-1">
            <ViewTab
              active={view === "moodboard"}
              onClick={() => onViewChange("moodboard")}
              icon={Sparkles}
              label="Moodboard"
              count={MOOD_ASSETS.length}
            />
            <ViewTab
              active={view === "canvases"}
              onClick={() => onViewChange("canvases")}
              icon={Layers}
              label="Canvases"
              count={0}
            />
          </div>
        </div>

        {/* Right: brief button */}
        <Button
          onClick={onOpenBrief}
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 rounded-full px-3 text-xs"
        >
          <FileText className="h-3.5 w-3.5" />
          Brief
        </Button>
      </div>
    </div>
  );
}

function ViewTab({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors",
        active
          ? "bg-card shadow-card text-foreground"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span className="text-xs text-muted-foreground/70">{count}</span>
    </button>
  );
}

// ─── Moodboard view ──────────────────────────────────────────────────
// Hosts the unified tab strip. First five tabs filter the asset grid;
// "Creative Direction" and "Briefing" swap to dedicated content panels.

function MoodboardView({
  tab,
  onTabChange,
  assets,
  generations,
  briefing,
  liveCdBrief,
  liveBrief,
  onPreview,
  onRefetch,
}: {
  tab: MoodboardTab;
  onTabChange: (t: MoodboardTab) => void;
  assets: MoodboardAsset[];
  generations: Record<string, GenerationSlot>;
  briefing: Briefing;
  liveCdBrief: unknown;
  liveBrief: unknown;
  onPreview: (a: MoodboardAsset) => void;
  onRefetch: () => void;
}) {
  const { setToast } = useStore();
  const tellDemoOnly = (text: string) => () => setToast({ kind: "ok", text });
  const isAssetTab =
    tab !== "creative_direction" &&
    tab !== "briefing" &&
    tab !== "shop_the_looks";

  const assetTabs: Array<{ key: AssetFilter; label: string }> = [
    { key: "all", label: "All" },
    { key: "World", label: "Worlds" },
    { key: "Character", label: "Characters" },
    { key: "Product", label: "Products" },
    { key: "Lifestyle", label: "Lifestyles" },
  ];

  return (
    <div className="px-6 md:px-8 py-6 space-y-6">
      {/* Tab strip — asset filters | content tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center rounded-full bg-card border border-border p-1 overflow-x-auto max-w-full">
          {assetTabs.map((t) => (
            <ChipBtn
              key={t.key}
              label={t.label}
              active={tab === t.key}
              onClick={() => onTabChange(t.key)}
            />
          ))}
          <span className="mx-1 h-5 w-px bg-border shrink-0" aria-hidden="true" />
          <ChipBtn
            label="Creative direction"
            active={tab === "creative_direction"}
            onClick={() => onTabChange("creative_direction")}
          />
          <ChipBtn
            label="Briefing"
            active={tab === "briefing"}
            onClick={() => onTabChange("briefing")}
          />
          {(briefing.catalogs?.length ?? 0) > 0 && (
            <ChipBtn
              label={`Shop the looks · ${briefing.catalogs!.length}`}
              active={tab === "shop_the_looks"}
              onClick={() => onTabChange("shop_the_looks")}
            />
          )}
        </div>
        {isAssetTab && (
          <div className="flex items-center gap-2">
            <Button
              onClick={tellDemoOnly("Approve-all is not wired up in this demo.")}
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Check className="h-3.5 w-3.5" />
              Approve all
            </Button>
            <Button
              onClick={tellDemoOnly(
                "Asset uploads are not wired up in this demo."
              )}
              size="sm"
              className="gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>
        )}
      </div>

      {/* Body — asset grid OR content panel */}
      {isAssetTab ? (
        <div className="space-y-6">
          {/* Generated images go to "All" only — the image_director
              subagent doesn't categorize into Worlds/Characters/Products/
              Lifestyles yet (Phase 6 territory). Showing them on the
              specific category tabs would be misleading. */}
          {tab === "all" && (
            <GenerationGrid generations={generations} />
          )}
          <AssetGrid assets={assets} onPreview={onPreview} />
        </div>
      ) : tab === "creative_direction" ? (
        <CreativeDirectionPanel briefing={briefing} live={liveCdBrief} />
      ) : tab === "briefing" ? (
        <BriefingBriefPanel briefing={briefing} live={liveBrief} />
      ) : (
        <ShopTheLooksTab briefing={briefing} onRefetch={onRefetch} />
      )}
    </div>
  );
}

function ShopTheLooksTab({
  briefing,
  onRefetch,
}: {
  briefing: Briefing;
  onRefetch: () => void;
}) {
  const catalogs = briefing.catalogs ?? [];
  const { setToast } = useStore();

  if (catalogs.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card/40 p-10 text-center">
        <p className="text-sm text-muted-foreground italic max-w-md mx-auto">
          No saved catalogs yet. When the agent extracts a product catalog
          from a file, hit "Save to Shop the Look" on the card to pin it
          here.
        </p>
      </div>
    );
  }

  const handleRemove = async (catalogId: string) => {
    try {
      await api.removeBriefingCatalog(briefing.id, catalogId);
      setToast({ kind: "ok", text: "Catalog removed." });
      onRefetch();
    } catch (e) {
      setToast({
        kind: "error",
        text: (e as Error).message || "Remove failed",
      });
    }
  };

  return (
    <div className="space-y-6">
      {catalogs.map((catalog) => (
        <SavedCatalogCard
          key={catalog.id}
          catalog={catalog}
          onRemove={() => handleRemove(catalog.id)}
        />
      ))}
    </div>
  );
}

function SavedCatalogCard({
  catalog,
  onRemove,
}: {
  catalog: SavedProductCatalog;
  onRemove: () => void;
}) {
  const totalProducts = catalog.looks.reduce(
    (sum, look) => sum + look.products.length,
    0
  );
  return (
    <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
      <div className="border-b border-border px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-1 min-w-0">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Saved catalog · {catalog.looks.length} look
            {catalog.looks.length !== 1 ? "s" : ""} · {totalProducts} products
          </p>
          <p className="font-medium truncate">
            {catalog.title || "Product catalog"}
          </p>
          {catalog.audience && (
            <p className="text-xs text-muted-foreground">
              Audience:{" "}
              <span className="font-mono uppercase tracking-wider">
                {catalog.audience}
              </span>
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          Remove
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
        {catalog.looks.map((look) => (
          <div
            key={look.id}
            className="rounded-md border border-border bg-background/40 p-3 space-y-2"
          >
            {look.hero_url && (
              <a
                href={look.hero_url}
                target="_blank"
                rel="noreferrer"
                className="block aspect-[3/4] overflow-hidden rounded border border-border bg-muted"
              >
                <img
                  src={look.hero_url}
                  alt={`Hero for ${look.title}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </a>
            )}
            <p className="text-sm font-medium">{look.title}</p>
            <ul className="space-y-0.5">
              {look.products.map((p, i) => (
                <li
                  key={i}
                  className="text-xs text-muted-foreground line-clamp-1"
                  title={[p.brand, p.color, p.code].filter(Boolean).join(" · ")}
                >
                  · {p.brand}
                  {p.color ? ` — ${p.color}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function GenerationGrid({
  generations,
}: {
  generations: Record<string, GenerationSlot>;
}) {
  const slots = Object.values(generations).sort(
    (a, b) => b.startedAt - a.startedAt
  );
  if (slots.length === 0) return null;
  const inFlight = slots.filter((s) => s.status === "generating").length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
          Generated this session · {slots.length}
          {inFlight > 0 ? ` · ${inFlight} in flight` : ""}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {slots.map((slot) => (
          <GenerationCard key={slot.slot_id} slot={slot} />
        ))}
      </div>
    </div>
  );
}

function GenerationCard({ slot }: { slot: GenerationSlot }) {
  const [open, setOpen] = useState(false);
  const aspectStyle = slot.aspect_ratio?.includes(":")
    ? { aspectRatio: slot.aspect_ratio.replace(":", " / ") }
    : { aspectRatio: "1 / 1" };

  if (slot.status === "ready" && slot.url) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "text-left rounded-xl border border-border bg-card overflow-hidden",
            "shadow-card hover:shadow-card-hover transition-all group"
          )}
          title={slot.prompt}
        >
          <div className="w-full overflow-hidden bg-muted" style={aspectStyle}>
            <img
              src={slot.url}
              alt="Generated"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              loading="lazy"
            />
          </div>
          <div className="p-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Generated · {slot.aspect_ratio || "1:1"}
            </p>
          </div>
        </button>
        {open && slot.url && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
            onClick={() => setOpen(false)}
          >
            <img
              src={slot.url}
              alt="Generated fullscreen"
              className="max-h-[90vh] max-w-[90vw] rounded-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </>
    );
  }

  if (slot.status === "failed") {
    return (
      <div
        className={cn(
          "rounded-xl border border-destructive/30 bg-destructive/5 p-4",
          "flex flex-col gap-2"
        )}
        style={aspectStyle}
        title={slot.error}
      >
        <p className="text-tiny tracking-[0.18em] uppercase text-destructive">
          Generation failed
        </p>
        {slot.error && (
          <p className="text-xs text-destructive/80 line-clamp-3">
            {slot.error}
          </p>
        )}
      </div>
    );
  }

  // Generating — pulsing skeleton with phase indicator. Slow shimmer is
  // the visual signal "this is in flight"; status text shows the latest
  // backend phase so power users can tell queued from running.
  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-muted/40 overflow-hidden",
        "relative flex flex-col"
      )}
      style={aspectStyle}
      title={slot.prompt || "Generating…"}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-muted/30 via-muted/60 to-muted/30 animate-pulse" />
      <div className="relative z-10 m-auto flex flex-col items-center gap-2 px-4 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {slot.phase || "queued"}
        </p>
      </div>
    </div>
  );
}

function ChipBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap",
        active
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      {label}
    </button>
  );
}

function AssetGrid({
  assets,
  onPreview,
}: {
  assets: MoodboardAsset[];
  onPreview: (a: MoodboardAsset) => void;
}) {
  if (assets.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card/40 p-10 text-center">
        <p className="text-sm text-muted-foreground italic max-w-md mx-auto">
          No assets yet. Generated images and approved references will land
          here as the agent produces them.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {assets.map((a) => (
          <button
            key={a.id}
            onClick={() => onPreview(a)}
            className="text-left rounded-xl border border-border bg-card overflow-hidden shadow-card hover:shadow-card-hover transition-all group"
          >
            <div
              className="aspect-square w-full relative"
              style={{ background: a.thumb }}
            >
              <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-md bg-black/40 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-0.5">
                {a.type}
              </span>
              {a.status === "approved" ? (
                <span className="absolute top-3 right-3 h-5 w-5 rounded-full bg-green-600 text-white flex items-center justify-center">
                  <Check className="h-3 w-3" />
                </span>
              ) : (
                <span className="absolute top-3 right-3 h-5 w-5 rounded-full bg-amber-500/90 text-white flex items-center justify-center">
                  <Star className="h-3 w-3" fill="currentColor" />
                </span>
              )}
            </div>
            <div className="p-3">
              <p className="text-sm font-medium line-clamp-1">{a.label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Palettes — hidden until the agent produces them. */}
      {PALETTES.length > 0 && (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-muted-foreground" />
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Palettes
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PALETTES.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-border bg-card p-4 shadow-card"
            >
              <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground mb-3">
                {p.name}
              </p>
              <div className="flex gap-1">
                {p.colors.map((c) => (
                  <div
                    key={c}
                    className="h-12 flex-1 rounded ring-1 ring-border/50"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

// ─── Creative Direction panel ────────────────────────────────────────
// Renders the cd_brief artifact when present, otherwise shows a stub
// scaffold matching what the CD agent typically produces during intake.

interface Section {
  heading: string;
  value: unknown;
}

const CD_BRIEF_STUB_SECTIONS: Section[] = [
  {
    heading: "Mission",
    value:
      "Establish BRAME as the default premium non-alc choice for Dry January, driving 40K new DTC customers and securing on-premise placement in 120 craft bars across NL/BE/DE.",
  },
  {
    heading: "Audience",
    value:
      "Wellness-curious urban professionals 28–42 who already shop craft (natural wine, specialty coffee) and view Dry January as an identity upgrade, not a deprivation.",
  },
  {
    heading: "Tone",
    value:
      "Considered, confident, never preachy. Adult and pleasure-forward — the language of a great hotel bar, not a wellness brand.",
  },
  {
    heading: "Key claims",
    value: [
      "Botanically distilled",
      "0.0% ABV, no compromise on body",
      "Made in Belgium, 5-year R&D",
      "Best paired with tonic + grapefruit peel",
    ],
  },
  {
    heading: "Mandatories",
    value: [
      "Always show product silhouette",
      "Pour ritual is the hero shot",
      "Never show a bar that obviously sells alcohol",
      "Pricing visible on retailer-led placements",
    ],
  },
];

function CreativeDirectionPanel({
  briefing,
  live,
}: {
  briefing: Briefing;
  live: unknown;
}) {
  const { activeTemplate } = useStore();
  const cd = briefing.cd_brief ?? live ?? null;
  const stub = templateToScaffoldSections(activeTemplate);
  const sections = useNormalizedSections(cd, stub);
  const fromAgent = !!cd;

  return (
    <BriefDocumentLayout
      label="Creative direction"
      title={briefing.title || "Untitled briefing"}
      fromAgent={fromAgent}
      stubMessage={
        activeTemplate
          ? `Showing the "${activeTemplate.name}" template — the CD agent will fill these sections during intake.`
          : "Showing scaffold — the CD agent will fill these sections during intake."
      }
      sections={sections}
    />
  );
}

// ─── Marketing Brief panel ──────────────────────────────────────────
const BRIEF_STUB_SECTIONS: Section[] = [
  {
    heading: "Objective",
    value:
      "Sustain awareness through Q1 by anchoring BRAME to the Dry January identity, then graduate consumers into year-round usage.",
  },
  {
    heading: "Target audience",
    value:
      "Primary — urban 28–42, wellness-curious, household income €60K+. Secondary — hospitality buyers procuring spec drinks.",
  },
  {
    heading: "Insight",
    value:
      "Dry January quitters cite \"missing the ritual,\" not the alcohol. The pour, the chill, the pairing — that's what BRAME replaces.",
  },
  {
    heading: "Single-minded message",
    value: "BRAME is the ritual you don't have to give up.",
  },
  {
    heading: "Channels",
    value: [
      "Meta + TikTok — 70% spend, hero + cutdowns",
      "OOH — 20%, premium urban placements",
      "Creator partnerships — 10%, food + interiors verticals",
    ],
  },
  {
    heading: "KPIs",
    value: [
      "40K new DTC customers",
      "120 on-premise placements",
      "Brand-search lift +35% vs. Dec baseline",
      "Cost per new customer ≤ €18",
    ],
  },
  {
    heading: "Timeline",
    value:
      "Week of 30 Dec → soft launch · Week of 6 Jan → broad push · Week of 20 Jan → midpoint readout · Week of 27 Jan → graduation push.",
  },
];

function BriefingBriefPanel({
  briefing,
  live,
}: {
  briefing: Briefing;
  live: unknown;
}) {
  const { activeTemplate } = useStore();
  const brief = briefing.brief ?? live ?? null;
  const stub = templateToScaffoldSections(activeTemplate);
  const sections = useNormalizedSections(brief, stub);
  const fromAgent = !!brief;

  return (
    <BriefDocumentLayout
      label="Marketing brief"
      title={briefing.title || "Untitled briefing"}
      fromAgent={fromAgent}
      stubMessage={
        activeTemplate
          ? `Showing the "${activeTemplate.name}" template — the brief lands here once the agent finishes intake.`
          : "Showing scaffold — the brief lands here once the agent finishes intake."
      }
      sections={sections}
    />
  );
}

// Build a scaffold from the active template's sections — empty values
// so the layout reads but the user can see what the agent is meant to
// fill. Falls back to the hardcoded stubs when no template is active.
function templateToScaffoldSections(
  template: ReturnType<typeof useStore>["activeTemplate"]
): Section[] {
  if (!template || template.sections.length === 0) {
    return CD_BRIEF_STUB_SECTIONS;
  }
  return template.sections.map((s) => ({
    heading: s.heading,
    value:
      s.type === "bullets"
        ? [s.description || "—"]
        : s.type === "object"
          ? { hint: s.description || "—" }
          : s.description || "—",
  }));
}

// ─── Document renderer ──────────────────────────────────────────────
// Walks any shape the agent emits and produces clean, hierarchical
// typography. Three distinct visual layers:
//   1. Section heading — Fraunces display-size title, divider below
//   2. Field label — uppercase tracked kicker
//   3. Body — comfortable 16px sans with relaxed leading
// Strings that look like JSON are parsed transparently, so the agent
// can emit either a parsed object or a JSON-formatted string and both
// render the same way.

function useNormalizedSections(raw: unknown, stub: Section[]): Section[] {
  const parsed = maybeParseJson(raw);
  if (parsed == null) return stub;
  if (typeof parsed === "string") return [{ heading: "Brief", value: parsed }];
  if (typeof parsed === "object") {
    return Object.entries(parsed as Record<string, unknown>).map(([k, v]) => ({
      heading: prettifyKey(k),
      value: v,
    }));
  }
  return stub;
}

// JSON-as-string is a real shape the agent emits — try to parse anything
// that opens with { or [ and treat the parsed result as the canonical
// value. Falls back to the raw string on parse failure (so prose with
// stray braces stays intact).
function maybeParseJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const head = trimmed[0];
  if (head !== "{" && head !== "[") return value;
  const tail = trimmed[trimmed.length - 1];
  if ((head === "{" && tail !== "}") || (head === "[" && tail !== "]")) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function prettifyKey(k: string): string {
  return k
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^(.)/, (c) => c.toUpperCase());
}

// Stable URL-friendly slug for in-page anchors.
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Document layout shared by Creative Direction + Briefing panels.
// Two columns on wide screens: sticky TOC on the left with active-section
// highlighting via IntersectionObserver, document sections on the right.
function BriefDocumentLayout({
  label,
  title,
  fromAgent,
  stubMessage,
  sections,
}: {
  label: string;
  title: string;
  fromAgent: boolean;
  stubMessage: string;
  sections: Section[];
}) {
  const sectionIds = sections.map(
    (s, i) => slugify(s.heading) || `section-${i}`
  );
  const activeId = useActiveSection(sectionIds);

  return (
    <div className="relative">
      {/* Header */}
      <header className="space-y-1.5 mb-6 lg:mb-8 max-w-3xl">
        <div className="flex items-center gap-2">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            {label}
          </p>
          {fromAgent && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[hsl(var(--inku-blue))/.10] text-[hsl(var(--inku-blue))]">
              From agent
            </span>
          )}
        </div>
        <h2 className="font-freight text-3xl font-light tracking-tight">
          {title}
        </h2>
        {!fromAgent && (
          <p className="text-xs text-muted-foreground italic">{stubMessage}</p>
        )}
      </header>

      {/* TOC + content split */}
      <div className="flex gap-10">
        <DocumentTOC
          sections={sections}
          ids={sectionIds}
          activeId={activeId}
        />
        <div className="flex-1 min-w-0 max-w-3xl">
          <DocumentSections sections={sections} ids={sectionIds} />
        </div>
      </div>
    </div>
  );
}

// Watches a list of section ids and returns whichever is currently
// closest to the top of the viewport. rootMargin compresses the
// "active zone" to the top quarter of the screen so a section feels
// active when its heading is near the top, not the moment it peeks in
// from the bottom.
function useActiveSection(ids: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
  const idsKey = ids.join("|");

  useEffect(() => {
    if (typeof window === "undefined" || ids.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          );
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      {
        // Active zone: top 30% of viewport.
        rootMargin: "0px 0px -70% 0px",
        threshold: 0,
      }
    );
    const observed: HTMLElement[] = [];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        observed.push(el);
      }
    }
    return () => {
      for (const el of observed) observer.unobserve(el);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return activeId;
}

function DocumentTOC({
  sections,
  ids,
  activeId,
}: {
  sections: Section[];
  ids: string[];
  activeId: string | null;
}) {
  if (sections.length <= 1) return null;
  return (
    <nav className="hidden lg:block w-48 shrink-0">
      <div className="sticky top-4 space-y-2.5">
        <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          On this page
        </p>
        <ul className="space-y-0.5 border-l border-border">
          {sections.map((s, i) => {
            const id = ids[i];
            const active = activeId === id;
            return (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const target = document.getElementById(id);
                    if (target) {
                      target.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }}
                  className={cn(
                    "block -ml-px pl-3 py-1.5 text-sm border-l transition-all",
                    active
                      ? "text-foreground border-[hsl(var(--inku-blue))] font-medium"
                      : "text-muted-foreground border-transparent hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  {s.heading}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

function DocumentSections({
  sections,
  ids,
}: {
  sections: Section[];
  ids: string[];
}) {
  return (
    <div className="space-y-4">
      {sections.map((s, i) => {
        const id = ids[i];
        return (
          <section
            key={id}
            id={id}
            className="rounded-xl border border-border bg-card shadow-card p-6 lg:p-7 space-y-4 scroll-mt-4"
          >
            <h3 className="font-freight text-2xl font-light tracking-tight leading-tight">
              {s.heading}
            </h3>
            <RichValue value={s.value} />
          </section>
        );
      })}
    </div>
  );
}

function RichValue({
  value: rawValue,
  depth = 0,
}: {
  value: unknown;
  depth?: number;
}): React.ReactNode {
  // Recursive JSON-as-string handling — agent sometimes nests stringified
  // JSON inside object values too.
  const value = maybeParseJson(rawValue);

  if (value == null || value === "") return null;

  if (typeof value === "string") {
    return (
      <p
        className={cn(
          "leading-[1.65] whitespace-pre-line text-foreground/85",
          depth === 0 ? "text-[15px]" : depth === 1 ? "text-[14.5px]" : "text-[13.5px]"
        )}
      >
        {value}
      </p>
    );
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return (
      <p className="text-sm font-mono text-foreground/90">{String(value)}</p>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null;

    // All-string array → em-dash bullet list (editorial).
    if (value.every((x) => typeof x === "string")) {
      return (
        <ul className="space-y-2 list-none">
          {(value as string[]).map((s, i) => (
            <li
              key={i}
              className="flex gap-3 text-[15px] leading-[1.6] text-foreground/85"
            >
              <span className="text-muted-foreground/60 select-none mt-[1px] shrink-0 font-mono text-sm">
                —
              </span>
              <span className="flex-1">{s}</span>
            </li>
          ))}
        </ul>
      );
    }

    // Object / mixed array → numbered blocks separated by hairline.
    return (
      <div className="space-y-5">
        {value.map((item, i) => (
          <div
            key={i}
            className={cn(
              "space-y-2",
              i > 0 && "pt-5 border-t border-border/40"
            )}
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/70">
              № {String(i + 1).padStart(2, "0")}
            </p>
            <RichValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v != null && v !== ""
    );
    if (entries.length === 0) return null;

    return (
      <div className={cn(depth === 0 ? "space-y-6" : "space-y-4")}>
        {entries.map(([k, v]) => (
          <FieldBlock key={k} label={prettifyKey(k)} depth={depth}>
            <RichValue value={v} depth={depth + 1} />
          </FieldBlock>
        ))}
      </div>
    );
  }

  return null;
}

function FieldBlock({
  label,
  depth,
  children,
}: {
  label: string;
  depth: number;
  children: React.ReactNode;
}) {
  // Top-level fields (under a section) get the strongest label treatment;
  // nested fields downscale and add a thin left rule for vertical lineage.
  const isTopField = depth === 0;
  return (
    <div
      className={cn(
        "space-y-1.5",
        !isTopField && "pl-4 border-l border-border/50"
      )}
    >
      <p
        className={cn(
          "font-mono uppercase font-medium text-muted-foreground",
          isTopField
            ? "text-[10.5px] tracking-[0.20em]"
            : "text-[10px] tracking-[0.18em]"
        )}
      >
        {label}
      </p>
      <div>{children}</div>
    </div>
  );
}

// ─── Canvases view ───────────────────────────────────────────────────
function CanvasesView() {
  const { setToast, navigate } = useStore();
  return (
    <div className="px-6 md:px-8 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Linked canvases
          </p>
          <h3 className="font-freight text-2xl font-light tracking-tight">
            Active workspaces
          </h3>
        </div>
        <Button
          onClick={() =>
            setToast({
              kind: "ok",
              text: "Linking canvases is not wired up in this demo.",
            })
          }
          size="sm"
          className="gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          New canvas
        </Button>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-card/40 p-12 text-center space-y-3">
        <Layers className="h-8 w-8 text-muted-foreground/40 mx-auto" />
        <p className="text-sm text-muted-foreground italic">
          No canvases linked to this briefing yet.
        </p>
        <p className="text-xs text-muted-foreground/70 max-w-md mx-auto">
          Once you've approved a moodboard, generate a canvas from this
          briefing to start producing on-brand variations.
        </p>
        <Button
          onClick={() => navigate("/canvas")}
          variant="ghost"
          size="sm"
          className="gap-1.5"
        >
          Browse canvases
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Brief slide-out sheet ───────────────────────────────────────────
function BriefSheet({
  open,
  onClose,
  briefing,
  intakeMode,
}: {
  open: boolean;
  onClose: () => void;
  briefing: Briefing;
  intakeMode: boolean;
}) {
  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-[150] bg-background/60 backdrop-blur-sm animate-fade-in"
      />
      <aside className="fixed right-0 top-0 z-[151] h-full w-full max-w-xl bg-card border-l border-border shadow-card-hover animate-fade-in flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
              Marketing brief
            </p>
            <h2 className="font-freight text-2xl font-light tracking-tight mt-0.5">
              {briefing.title || "Untitled briefing"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-accent flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {intakeMode ? (
            <div className="rounded-lg border border-dashed border-border bg-card/40 p-8 text-center space-y-2">
              <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto" />
              <p className="text-sm text-muted-foreground italic">
                Brief not produced yet.
              </p>
              <p className="text-xs text-muted-foreground/70 max-w-sm mx-auto">
                Continue the agent intake on the left — once enough context
                is gathered, the brief will land here automatically.
              </p>
            </div>
          ) : briefing.brief ? (
            <pre className="text-sm whitespace-pre-wrap font-sans text-foreground/90 leading-relaxed">
              {typeof briefing.brief === "string"
                ? briefing.brief
                : JSON.stringify(briefing.brief, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Brief content will appear here once landed.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
